// SEQ-180 phase 2: the metric vocabulary the widget editor picks from.
//
// The TypeScript registry (DEFAULT_PNL_METRIC_REGISTRY, pnlAnalytics.ts) is
// the engine's own copy and carries no display name, because the engine never
// renders one. `pnl_metric_registry` does, and it is also the only place an
// organisation's custom operational metrics exist (SEQ-182 T14), so the
// picker reads the table and falls back to the TypeScript registry with a
// humanised key when the table cannot be read. That fallback is what keeps
// the builder usable on a database where the phase 1 migrations have not been
// applied yet, matching the page's own DEV fixture fallback.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { DEFAULT_PNL_METRIC_REGISTRY } from "../../lib/pnl/pnlAnalytics";
import { humaniseMetricKey, type PnlMetricOption } from "./metricOption";
import { decodeRegistryRows, type RawRegistryRow } from "./registryRowDecode";

export type { PnlMetricOption };

const REGISTRY_COLUMNS =
  "metric_key, organization_id, section, display_name, kind, unit, sort_order, is_active, is_deductible, metric_role, financial_inclusion, aggregation_method, decimals";

function fallbackOptions(): PnlMetricOption[] {
  return DEFAULT_PNL_METRIC_REGISTRY.map((def, index) => ({
    ...def,
    displayName: humaniseMetricKey(def.metricKey),
    sortOrder: index,
    isCustom: false,
  }));
}

export interface UsePnlMetricRegistryResult {
  metrics: PnlMetricOption[];
  loading: boolean;
  /** True when the table could not be read and the built-in vocabulary is standing in. */
  usingFallback: boolean;
  /**
   * M3: set when the table was read but fails `assertRegistryConsistent`
   * (registryRowDecode.ts). The page shows it as an error and the built-in
   * vocabulary stands in, so an inconsistent registry is visible rather
   * than silently defaulted around.
   */
  registryError: string | null;
}

/* eslint-disable @typescript-eslint/no-explicit-any -- pnl_metric_registry is not in the generated Supabase types (SEQ-180/182) */
export function usePnlMetricRegistry(): UsePnlMetricRegistryResult {
  const [rows, setRows] = useState<PnlMetricOption[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [registryError, setRegistryError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (supabase as any)
      .from("pnl_metric_registry")
      .select(REGISTRY_COLUMNS)
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .then(({ data, error }: { data: RawRegistryRow[] | null; error: { message: string } | null }) => {
        if (cancelled) return;
        if (error || !data || data.length === 0) {
          setRows(null);
        } else {
          const decoded = decodeRegistryRows(data);
          if (decoded.error !== null) {
            setRegistryError(`The metric registry is inconsistent, so the built-in metric list is being used. ${decoded.error}`);
            setRows(null);
          } else {
            setRegistryError(null);
            setRows(decoded.options);
          }
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const metrics = useMemo(() => rows ?? fallbackOptions(), [rows]);

  return { metrics, loading, usingFallback: rows === null && !loading, registryError };
}
/* eslint-enable @typescript-eslint/no-explicit-any */
